import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { PanelLeft, PanelRight, LogOut, Home } from "lucide-react";
import PasswordGate from "./viewer/PasswordGate";
import Sidebar from "./viewer/Sidebar";
import MetaPanel from "./viewer/MetaPanel";
import Map2D from "./viewer/Map2D";
import Scene3D from "./viewer/Scene3D";
import { getToken, clearToken, listDatasets } from "./viewer/api";
import "./viewer/viewer.css";

export default function Viewer() {
  const [authed, setAuthed] = useState(!!getToken());
  const [datasets, setDatasets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [viewMode, setViewMode] = useState("2d");
  const [basemap, setBasemap] = useState("dark");
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);

  // Per-mode controls
  const [exaggeration, setExaggeration] = useState(1.6);
  const [overlayOpacity, setOverlayOpacity] = useState(0.85);
  const [measureMode, setMeasureMode] = useState(null);
  const [liveCoord, setLiveCoord] = useState(null);

  const refreshDatasets = useCallback(async () => {
    try {
      const list = await listDatasets();
      setDatasets(list);
      if (!activeId && list.length) setActiveId(list[0].id);
    } catch (err) {
      if (err.message === "UNAUTHORIZED") {
        clearToken(); setAuthed(false);
      } else {
        toast.error("Failed to load datasets");
      }
    }
  }, [activeId]);

  useEffect(() => {
    if (authed) refreshDatasets();
  }, [authed, refreshDatasets]);

  if (!authed) {
    return (
      <>
        <Toaster theme="dark" position="top-right" />
        <PasswordGate onAuthed={() => setAuthed(true)} />
      </>
    );
  }

  const active = datasets.find((d) => d.id === activeId);

  return (
    <div className="tbg-vw" data-testid="viewer-page">
      <Toaster theme="dark" position="top-right" />

      {/* Top bar */}
      <div className="tbg-vw-top">
        <Link to="/" className="tbg-vw-brand" data-testid="viewer-home-link">
          <img
            src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png"
            alt="TBG"
          />
          <span className="sub">Viewer</span>
        </Link>

        <div className="tbg-vw-mode" role="tablist" aria-label="View mode">
          <button
            className={viewMode === "2d" ? "active" : ""}
            onClick={() => setViewMode("2d")}
            data-testid="viewer-mode-2d"
          >2D</button>
          <button
            className={viewMode === "3d" ? "active" : ""}
            onClick={() => setViewMode("3d")}
            data-testid="viewer-mode-3d"
          >3D</button>
        </div>

        {viewMode === "2d" && (
          <select
            className="tbg-vw-basemap"
            value={basemap}
            onChange={(e) => setBasemap(e.target.value)}
            data-testid="viewer-basemap-select"
          >
            <option value="dark">Dark</option>
            <option value="satellite">Satellite</option>
            <option value="topo">Topographic</option>
            <option value="osm">OpenStreetMap</option>
          </select>
        )}

        <div className="tbg-vw-actions">
          <Link to="/" className="tbg-vw-btn ghost" data-testid="viewer-back-home">
            <Home size={12} /> Site
          </Link>
          <button
            className="tbg-vw-btn ghost"
            onClick={() => { clearToken(); setAuthed(false); }}
            data-testid="viewer-logout"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>

      {/* Main body */}
      <div className={`tbg-vw-body ${leftOpen ? "" : "left-closed"} ${rightOpen ? "" : "right-closed"}`}>
        {/* Left panel */}
        {leftOpen ? (
          <div className="tbg-vw-panel" data-testid="viewer-left-panel">
            <div className="tbg-vw-panel-head">
              <span>Project Tree</span>
              <button onClick={() => setLeftOpen(false)} title="Collapse" data-testid="viewer-left-collapse">
                <PanelLeft size={13} />
              </button>
            </div>
            <div className="tbg-vw-panel-body">
              <Sidebar
                datasets={datasets}
                activeId={activeId}
                onSelect={setActiveId}
                onReload={refreshDatasets}
              />
            </div>
          </div>
        ) : (
          <div className="tbg-vw-panel">
            <div className="tbg-vw-rail">
              <button onClick={() => setLeftOpen(true)} title="Open" data-testid="viewer-left-open">
                <PanelLeft size={13} />
              </button>
              <span className="lbl">Project</span>
            </div>
          </div>
        )}

        {/* Main viewport */}
        <div className="tbg-vw-main" data-testid="viewer-viewport">
          {!active && (
            <div className="tbg-vw-empty">
              <div className="t">No Dataset</div>
              <div className="s">Select a dataset from the project tree.</div>
            </div>
          )}
          {active && viewMode === "2d" && (
            <Map2D
              dataset={active}
              basemap={basemap}
              overlayOpacity={overlayOpacity}
              measureMode={measureMode}
              onCoord={setLiveCoord}
            />
          )}
          {active && viewMode === "3d" && (
            <Scene3D dataset={active} exaggeration={exaggeration} />
          )}
        </div>

        {/* Right panel */}
        {rightOpen ? (
          <div className="tbg-vw-panel right" data-testid="viewer-right-panel">
            <div className="tbg-vw-panel-head">
              <span>Inspector</span>
              <button onClick={() => setRightOpen(false)} title="Collapse" data-testid="viewer-right-collapse">
                <PanelRight size={13} />
              </button>
            </div>
            <div className="tbg-vw-panel-body">
              <MetaPanel
                dataset={active}
                viewMode={viewMode}
                exaggeration={exaggeration}
                setExaggeration={setExaggeration}
                overlayOpacity={overlayOpacity}
                setOverlayOpacity={setOverlayOpacity}
                measureMode={measureMode}
                setMeasureMode={setMeasureMode}
                liveCoord={liveCoord}
              />
            </div>
          </div>
        ) : (
          <div className="tbg-vw-panel right">
            <div className="tbg-vw-rail">
              <button onClick={() => setRightOpen(true)} title="Open" data-testid="viewer-right-open">
                <PanelRight size={13} />
              </button>
              <span className="lbl">Inspector</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
